import { create, useModal } from '@ebay/nice-modal-react';
import Divider from '@shared/components/Divider/Divider';
import Popup from '@shared/components/Popup/Popup';
import { EventType, HistoryVisibility, JoinRule } from 'matrix-js-sdk/src/matrix';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ModalFooter from 'components/ModalFooter';
import useFeatureFlagContext from 'contexts/FeatureFlagProvider/useFeatureFlagContext';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useMeetingGroupChatCreation from 'hooks/roomCreation/useMeetingGroupChatCreation';
import useRoomAccessInitialState from 'hooks/roomCreation/useRoomAccessInitialState';
import useRoomCreationPowerLevels from 'hooks/roomCreation/useRoomCreationPowerLevels';
import useRoomManagersInitialState from 'hooks/roomCreation/useRoomManagersInitialState';
import { buildMeetingInitialStates, encryptionState } from 'hooks/useCreateMeeting/utils';
import { buildWorkspaceMeetingCreationContext } from 'hooks/useCreateMeeting/workspaceCreationParams';
import useCreateRoom from 'hooks/useCreateRoom/useCreateRoom';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { setPreRoomBackPathToVideoHub } from 'hooks/useSessionState';
import { isMatrixIdFormat } from 'modules/CreateWorkspaceModal/WorkspaceInviteStep/WorkspaceInviteForm/utils';
import useMatrixInviteeValidation from 'modules/Messenger/hooks/useMatrixInviteeValidation';
import { registerInstantMeeting } from 'modules/VideoMeetings/instantCall/registerInstantMeeting';
import GroupChatToggleField from 'modules/VideoMeetings/ScheduleMeetingModal/GroupChatToggleField';
import InviteParticipantsSection from 'modules/VideoMeetings/ScheduleMeetingModal/InviteParticipantsSection';
import { linkMeetingGroupChat } from 'modules/VideoMeetings/ScheduleMeetingModal/linkMeetingGroupChat';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { getIsFromGuestServer } from 'modules/Workspaces/WorkspacesProvider/utils';
import { FeatureFlag } from 'utils/Constants';
import logger from 'utils/logging/faro';
import {
  buildMeetingSpaceChatChildInitialState,
  CustomMeedioMatrixEvents,
  getViaServers,
  inviteUsersToRoom,
  MeedioCallType,
  sendChildRoomParentStates,
} from 'utils/matrixUtils';
import { isGuestModeEnabled } from 'utils/utils';

export type InstantCallModalProps = {
  mode?: 'create' | 'invite';
  /** Required when `mode` is `'invite'`. */
  roomId?: string;
  copyLinkUrl?: string;
  /** When opened from a Messenger chat — auto-link; hides group-chat toggle. */
  messengerRoomId?: string;
};

const InstantCallModal = create((props: InstantCallModalProps = {}) => {
  const { t } = useTranslation();
  const { remove } = useModal(InstantCallModal);
  const { matrixClient } = useMatrixContext();
  const { isEnabled } = useFeatureFlagContext();
  const { createRoom } = useCreateRoom();
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const { workspaceId, workspacePermissions } = useWorkspacesContext();
  const roomManagersInitialState = useRoomManagersInitialState(workspaceId);
  const { ownerPowerLevel: meetingOwnerPowerLevel } = useRoomCreationPowerLevels('meeting');
  // BUG-003: the meeting-room group chat is a messenger room → chat-scoped power levels (matching the
  // /messenger path), not the meeting-scoped ones used for the meeting space + dedicated chat-child rooms.
  // The hook pre-binds the workspace context + chat scope; the meeting space/chat-child stay meeting-scoped.
  const { createMeetingGroupChat } = useMeetingGroupChatCreation();
  const teamRoomAccessInitialState = useRoomAccessInitialState('team');
  const { filterInvalidMatrixInvitees } = useMatrixInviteeValidation();
  const pushNoPermissionToast = useNoPermissionToast();
  const [isLoading, setIsLoading] = useState(false);
  const [participants, setParticipants] = useState<string[]>([]);
  const [groupChat, setGroupChat] = useState(false);

  const isRoomE2EEEnabled = isEnabled(FeatureFlag.ROOM_E2EE);
  const matrixInviteeIds = participants.filter(isMatrixIdFormat);
  const meetingTitle = t('video_meeting');
  const isInviteMode = props.mode === 'invite';
  const userId = matrixClient.getUserId();
  const isGuestUser = isGuestModeEnabled() && getIsFromGuestServer(userId ?? '');
  const showGroupChatToggle = !isInviteMode && !props.messengerRoomId && !isGuestUser;
  const displayName = (userId ? matrixClient.getUser(userId)?.displayName : undefined) ?? userId ?? '';
  const groupChatName = t('schedule_meeting.group_chat_room_name', { name: displayName });

  const handleStartCall = async () => {
    // Defence in depth (R1.4): block create-submit when the workspace role lacks instant-call permission.
    if (workspaceId && !workspacePermissions.createInstantMeeting) {
      pushNoPermissionToast();
      return;
    }

    setIsLoading(true);
    let spaceRoomId: string | undefined;
    let chatRoomId: string | undefined;

    try {
      // BUG-004: pre-validate Matrix invitees (drop non-existent IDs + warn) before creating any room or
      // sending invites — mirroring the /messenger path (useCreateMessengerRoom). Done before room
      // creation so a validation failure leaves no orphaned rooms.
      const validMxids = await filterInvalidMatrixInvitees(matrixInviteeIds);

      // BUG-002: reuse the shared meeting-space builder (gated on ROOM_E2EE) instead of hand-assembling
      // the workspace-parent state. This encrypts the instant-call space when the flag is on — matching
      // the Schedule / Create room paths (useCreateMeeting) and the sibling instant-call path
      // (useCreateMessengerCall). With isPrivateRoom/isWaitingListEnabled false and restrictedToRoomId
      // null, this returns [encryptionState?] + [canonical-parent when workspaceId] — identical to the
      // previous workspaceParentStates when the flag is off. BUG-003: the dedicated chat child is now
      // also encrypted when the flag is on (see the customInitialStates spread below), matching the space
      // and the reference paths (useCreateMeeting / useScheduleMeetingSubmit).
      const meetingStates = buildMeetingInitialStates({
        isPrivateRoom: false,
        isWaitingListEnabled: false,
        workspaceId,
        restrictedToRoomId: null,
        matrixClient,
        isRoomE2EEEnabled,
      });

      // Workspace parity (R2.1): bot + meeting overrides + creator PL 99 on both rooms; managers +
      // team access events. Personal workspace (workspaceId === null) stays byte-for-byte unchanged.
      const { managersStates, accessStates, workspaceCreationParams } = buildWorkspaceMeetingCreationContext({
        workspaceId,
        roomManagersInitialState,
        teamRoomAccessInitialState,
        meetingOwnerPowerLevel,
      });

      // Only create a dedicated chat child for the plain Video Hub instant call. When the call is
      // started from a Messenger chat (messengerRoomId) or with the Group chat toggle ON,
      // linkMeetingGroupChat / postLinkMessengerRoom links the reused room as the single chat child,
      // so a dedicated child would orphan it (chatChildEvents[0] wins). Mirrors useScheduleMeetingSubmit.
      if (!props.messengerRoomId && !groupChat) {
        const chat = await createRoom({
          title: meetingTitle,
          customInitialStates: [
            {
              type: EventType.RoomHistoryVisibility,
              state_key: '',
              content: { history_visibility: HistoryVisibility.Invited },
            },
            ...managersStates,
            ...(isRoomE2EEEnabled ? [encryptionState] : []),
          ],
          ...workspaceCreationParams,
        });
        chatRoomId = chat.room_id;
      }

      const space = await createRoom({
        title: meetingTitle,
        customCreationContent: {
          type: 'm.space',
          [CustomMeedioMatrixEvents.INSTANT_CALL_ROOM_TYPE]: true,
        },
        customInitialStates: [
          {
            type: CustomMeedioMatrixEvents.CALL_TYPE,
            state_key: '',
            content: { call_type: MeedioCallType.Instant },
          },
          ...meetingStates,
          ...managersStates,
          ...accessStates,
          ...(chatRoomId ? [buildMeetingSpaceChatChildInitialState(chatRoomId)] : []),
        ],
        ...workspaceCreationParams,
      });
      spaceRoomId = space.room_id;

      if (chatRoomId) {
        await sendChildRoomParentStates(matrixClient, chatRoomId, space.room_id, JoinRule.Restricted);
      }

      if (workspaceId) {
        const viaServers = getViaServers(workspaceId, matrixClient);
        await matrixClient
          .sendStateEvent(workspaceId, EventType.SpaceChild, { via: viaServers, suggested: true }, space.room_id)
          .catch((err: unknown) => {
            logger.warn('InstantCallModal: failed to write SpaceChild to workspace', { error: err });
          });
      }

      const meeting = await registerInstantMeeting({
        matrixClient,
        spaceRoomId: space.room_id,
        meetingTitle,
        logLabel: 'InstantCallModal',
        extraMeetingFields: {
          messenger_room_id: props.messengerRoomId,
          is_instant: true,
          ...(workspaceId ? { workspace_matrix_room_id: workspaceId } : {}),
        },
      });

      if (meeting) {
        // The meeting-room group chat is created through the shared useMeetingGroupChatCreation hook
        // (workspace context + chat-scoped PLs pre-bound), keeping linkMeetingGroupChat a pure, hook-free
        // async function that just receives a { name, participantMxids } creator.
        await linkMeetingGroupChat({
          matrixClient,
          matrixRoomId: space.room_id,
          meeting,
          meetingTitle,
          participantMxids: validMxids,
          groupChatEnabled: groupChat || !!props.messengerRoomId,
          messengerRoomId: props.messengerRoomId,
          workspaceId,
          isInstant: true,
          groupChatName,
          createMessengerRoom: createMeetingGroupChat,
        });
      }

      // #8237 (BUG-001): invite to meeting space AFTER linkMeetingGroupChat so that the restricted
      // join-rules are already in place when the invitee's client syncs the meeting-space invite.
      if (validMxids.length > 0) {
        const { failedInvites } = await inviteUsersToRoom(matrixClient, space.room_id, validMxids);
        if (failedInvites.length > 0) {
          pushToast({
            variant: failedInvites.length === validMxids.length ? 'error' : 'warning',
            title: t('messenger.some_users_not_invited', { users: failedInvites.join(', ') }),
          });
        }
      }

      remove();
      // Instant meetings have no alias (#8263) — address the room by its Matrix room ID so URLs
      // and invite links use `matrix_room_id`. Record the video hub as the origin (#8073) so the
      // pre-room back button returns to `/meetings`.
      setPreRoomBackPathToVideoHub();
      navigate(`/rooms/${space.room_id}`);
    } catch {
      setIsLoading(false);
      if (spaceRoomId) {
        matrixClient.leave(spaceRoomId).catch((err: unknown) => {
          logger.warn('InstantCallModal: failed to leave space room on error', { error: err });
        });
      }
      if (chatRoomId) {
        matrixClient.leave(chatRoomId).catch((err: unknown) => {
          logger.warn('InstantCallModal: failed to leave chat room on error', { error: err });
        });
      }
      pushToast({ variant: 'error', title: t('create_room_error') });
    }
  };

  const handleInviteToRoom = async () => {
    const { roomId } = props;
    if (props.mode !== 'invite' || !roomId || matrixInviteeIds.length === 0) {
      remove();
      return;
    }

    setIsLoading(true);
    try {
      const { failedInvites } = await inviteUsersToRoom(matrixClient, roomId, matrixInviteeIds);
      if (failedInvites.length > 0) {
        pushToast({
          variant: failedInvites.length === matrixInviteeIds.length ? 'error' : 'warning',
          title: t('messenger.some_users_not_invited', { users: failedInvites.join(', ') }),
        });
      }
      remove();
    } catch (error) {
      logger.error('InstantCallModal: failed to invite members', { error, roomId });
      pushToast({ variant: 'error', title: t('errors.something_went_wrong') });
    } finally {
      setIsLoading(false);
    }
  };

  const headerTitle = isInviteMode ? t('video_hub.invite_participants_label') : t('instant_call_modal_title');
  const successLabel = isInviteMode ? t('invite') : t('start_call');
  const handleSubmit = isInviteMode ? handleInviteToRoom : handleStartCall;

  return (
    <Popup isVisible closePopup={remove}>
      <Popup.FullScreenContainer className="md:w-[573px]">
        <Popup.Header className="!mx-0 !shadow-none !border-none px-4 pt-4 !justify-start">
          <div className="flex w-full items-center justify-between">
            <span className="text-size-md font-medium">{headerTitle}</span>
            <Popup.CloseIcon closePopup={remove} className="!static" />
          </div>
        </Popup.Header>
        <div className="px-4">
          <Divider />
        </div>
        <div className="flex grow flex-col gap-4 overflow-y-auto px-4 py-4">
          {showGroupChatToggle && <GroupChatToggleField checked={groupChat} onCheckedChange={setGroupChat} />}
          <InviteParticipantsSection
            inviteInputMode="matrixIdOnly"
            participants={participants}
            onParticipantsChange={setParticipants}
            meetingUrl={props.mode === 'invite' ? props.copyLinkUrl : undefined}
          />
        </div>
        <div className="px-4">
          <Divider />
        </div>
        <ModalFooter
          onCloseClick={remove}
          onSubmit={handleSubmit}
          successButtonText={successLabel}
          cancelText={t('cancel')}
          isLoading={isLoading}
          successDisabled={isLoading || (isInviteMode && matrixInviteeIds.length === 0)}
          hasBorder={false}
        />
      </Popup.FullScreenContainer>
    </Popup>
  );
});

export default InstantCallModal;
